import './SelectField.scss';
import { FieldPath } from '@form-ts/core';
import { useFormField, useFormWatch } from '@form-ts/react';
import { pipe } from 'fp-ts/function';
import React, { ReactElement, ReactNode, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { GroupBase, InputActionMeta, SingleValue } from 'react-select';
import { SearchableSelectArea, SearchableSelectAreaProps } from 'src/modules/common/components/SearchableSelectArea';
import { FormError } from 'src/modules/form/types/FormError';

type Option<TValue> = {
  label: ReactNode;
  value: TValue;
};

type Props<
  TData,
  TValue,
  TOption = Option<TValue>,
  Group extends GroupBase<TOption> = GroupBase<TOption>,
> = Omit<SearchableSelectAreaProps<TOption, Group>,
| 'id'
| 'name'
| 'isMulti'
| 'onChange'
| 'onBlur'
| 'onInputChange'
| 'inputValue'
| 'options'
| 'value'
| 'errorMessage'
| 'classNamePrefix'
| 'closeMenuOnSelect'
| 'isSearchable'
| 'isClearable'
| 'components'
| 'styles'
| 'menuShouldScrollIntoView'
> & {
  readonly field: FieldPath<TData, FormError, TValue>;
  readonly options: ReadonlyArray<Option<TValue>>;
  readonly renderError?: (error: FormError) => React.ReactNode;
};

export const SearchableSelectField = <
  TData,
  TValue,
  Group extends GroupBase<Option<TValue>> = GroupBase<Option<TValue>>>(
  {
    field,
    options,
    renderError = renderErrorDefault,
    ...props
  }: Props<TData, TValue, Option<TValue>, Group>): ReactElement => {
  const { meta, value, error } = useFormField(field);

  const [search, setSearch] = useState('');

  const submitted = useFormWatch(field.form, field.form.submitted.get);
  const invalid = error !== undefined && (meta.touched || submitted);

  const handleChange = useCallback((selectedItem: SingleValue<Option<TValue>>) => {
    if (selectedItem) {
      field.form.change(pipe(
        field.form.currentState,
        field.value.set(selectedItem.value),
      ));
    }
  }, [field]);

  const handleBlur = useCallback(() => {
    field.form.change(pipe(
      field.form.currentState,
      field.touched.set(true),
    ));
  }, [field]);

  const onInputChange = useCallback((newValue: string, actionMeta: InputActionMeta) => {
    if (actionMeta.action !== 'input-blur' && actionMeta.action !== 'menu-close') {
      setSearch(newValue);
    }
  }, []);

  return (
    <SearchableSelectArea
      {...props}
      id={`${field.form.name}.${field.path}`}
      name={field.path}
      isMulti={false}
      onChange={handleChange}
      onBlur={handleBlur}
      onInputChange={onInputChange}
      inputValue={search}
      options={options}
      value={options.find((option) => option.value === value) ?? null}
      errorMessage={invalid && error ? renderError(error) : null}
    />
  );
};

const renderErrorDefault = (error: FormError): React.ReactNode => (
  <FormattedMessage id={`form/error/${error.code}`} values={error.context}/>
);
